import type { Provider } from '@angular/core';
import { ElementRef, Inject, Injectable, InjectionToken, Optional } from '@angular/core';

import { animationFrameScheduler, iif, Observable, of, Subject, switchMap } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, share } from 'rxjs/operators';

import { filterBoolean } from '@core/common-legacy';
import { macroTask } from '@core/utils-legacy';

import { ScrollableDirective } from '../core/overlay/scroll/scrollable.directive';
import { RtPlatform } from '../core/platform/platform';

export interface IntersectionObserverAreaConfig {
  observerDelayFrames?: number;
  debounceTime?: number;
  // https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#Intersection_observer_options
  intersectionOptions?: {
    threshold: number | number[];
    rootMargin: string;
  };
}

export const IntersectionObserverAreaConfigToken =
  new InjectionToken<IntersectionObserverAreaConfig>('IntersectionObserverAreaConfig');

type IntersectionEntryWithVisibility = {
  entry: IntersectionObserverEntry;
  visible: boolean;
};

@Injectable()
class IntersectionObserverAreaDestroyHook {
  private callback: Function;

  public register(callback: Function) {
    this.callback = callback;
  }

  public ngOnDestroy() {
    if (this.callback) this.callback();
  }
}

/**
 * Provide this class in component which will act as visibility bounds for children (observed) components.
 *
 * Read more: https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#Creating_an_intersection_observer
 *
 * TODO: for ease of use, we might want to wrap this in component:
 * (<rt-intersection-observer-area></rt-intersection-observer-area>)
 */

@Injectable()
export class IntersectionObserverArea {
  private readonly intersection: IntersectionObserver;

  public serviceAvailable: boolean;

  private initializeObservers$: Observable<void>;
  private entries = new Subject<IntersectionObserverEntry[]>();
  private observedElementsMap: WeakMap<Element, Observable<IntersectionEntryWithVisibility>> =
    new WeakMap();

  private queue$ = new Subject<void>();

  constructor(
    private ref: ElementRef,
    private platform: RtPlatform,
    @Inject(IntersectionObserverAreaConfigToken) private config: IntersectionObserverAreaConfig,
    @Optional() private dialogDestroyHook?: IntersectionObserverAreaDestroyHook,
  ) {
    this.serviceAvailable = this.checkFeatureAvailability();

    if (this.serviceAvailable) {
      if (this.dialogDestroyHook) this.dialogDestroyHook.register(() => this.ngOnDestroy());

      this.initializeObservers$ = iif(
        () => this.config.observerDelayFrames! > 0,
        this.queue$.pipe(debounceTime(this.config.observerDelayFrames!, animationFrameScheduler)),
        of(void 0),
      ).pipe(share());

      this.intersection = new IntersectionObserver(
        (entries: IntersectionObserverEntry[]) => {
          this.entries.next(entries);
        },
        {
          root: ref.nativeElement,
          ...config.intersectionOptions,
        },
      );
    }
  }

  public ngOnDestroy() {
    this.disconnect();
  }

  public intersectionChange(el: ElementRef): Observable<IntersectionEntryWithVisibility> {
    macroTask(() => this.queue$.next());
    return this.initializeObservers$.pipe(
      switchMap(() => {
        const observable$ = this.observeElement(el.nativeElement);
        return this.config.debounceTime! > 0
          ? observable$.pipe(debounceTime(this.config.debounceTime!))
          : observable$;
      }),
    );
  }

  public disconnect(): void {
    if (this.intersection) {
      this.intersection.disconnect();
      this.entries.complete();
    }
  }

  public unobserve(el: Element): void {
    this.intersection.unobserve(el);
    this.observedElementsMap.delete(el);
  }

  public isEntryVisible(entry: IntersectionObserverEntry): boolean {
    return (
      entry.isIntersecting &&
      entry.boundingClientRect.width > 0 &&
      entry.boundingClientRect.height > 0
    );
  }

  private observeElement(el: Element): Observable<IntersectionEntryWithVisibility> {
    if (this.observedElementsMap.has(el)) {
      return this.observedElementsMap.get(el)!;
    }

    const intersectionEntry$ = new Observable<IntersectionEntryWithVisibility>(observer => {
      const sub = this.entries
        .pipe(
          map(entries => entries.find(entry => entry.target === el)),
          filterBoolean,
          map(entry => ({ entry, visible: this.isEntryVisible(entry) })),
          distinctUntilChanged((a, b) => a.visible === b.visible),
        )
        .subscribe(observer);

      return () => {
        this.unobserve(el);
        sub.unsubscribe();
      };
    });

    this.observedElementsMap.set(el, intersectionEntry$);
    this.intersection.observe(el);

    return intersectionEntry$;
  }

  private checkFeatureAvailability(): boolean {
    // https://caniuse.com/#feat=intersectionobserver

    try {
      if ('IntersectionObserver' in window) {
        if (this.platform.EDGE) {
          const [_, major] = this.platform.ua.match(/Edge\/(\d+)/)!;
          return parseInt(major, 10) > 15;
        }

        if (this.platform.CHROME) {
          const [_, major] = this.platform.ua.match(/Chrome\/(\d+)/)!;
          return parseInt(major, 10) > 57;
        }

        if (this.platform.OPERA) {
          const [_, major] = this.platform.ua.match(/OPR\/(\d+)/)!;
          return parseInt(major, 10) > 44;
        }

        return true;
      } else {
        return false;
      }
    } catch (err) {
      return false;
    }
  }
}

export const defaultIntersectionObserverAreaConfig: IntersectionObserverAreaConfig = {
  intersectionOptions: {
    rootMargin: '0px',
    threshold: [0.0, 1.0],
  },
};

export function provideIntersectionObserverArea(
  config: IntersectionObserverAreaConfig = {},
): Provider[] {
  return [
    {
      provide: IntersectionObserverAreaConfigToken,
      useValue: {
        ...defaultIntersectionObserverAreaConfig,
        ...config,
      },
    },
    IntersectionObserverArea,
  ];
}

export function provideIntersectionObserverAreaForDialog(
  config: IntersectionObserverAreaConfig = {},
) {
  return [
    {
      provide: IntersectionObserverAreaConfigToken,
      useValue: {
        ...defaultIntersectionObserverAreaConfig,
        ...config,
      },
    },
    IntersectionObserverAreaDestroyHook,
    {
      provide: IntersectionObserverArea,
      useFactory: (
        scrollable: ScrollableDirective,
        platform: RtPlatform,
        config: IntersectionObserverAreaConfig,
        destroy: IntersectionObserverAreaDestroyHook,
      ) => new IntersectionObserverArea(scrollable.getElementRef(), platform, config, destroy),
      deps: [
        ScrollableDirective,
        RtPlatform,
        [Inject, IntersectionObserverAreaConfigToken],
        [Inject, Optional, IntersectionObserverAreaDestroyHook],
      ],
    },
  ];
}
